import { addBreadcrumb } from '@sentry/react';

import { Image, ImageProps } from '@chakra-ui/react';

import fullLogoBlack from 'app/assets/svg/fullLogoBlack.svg';
import fullLogoWhite from 'app/assets/svg/fullLogoWhite.svg';

import { companyLogoFor, useCompanyBranding } from 'app/contexts/Branding';

interface BrandLogoProps extends Omit<ImageProps, 'src' | 'alt' | 'onError'> {
  /** Which of the company's logos suits the ground it sits on. */
  variant?: 'onLight' | 'onDark';
}

const ASLAN_LOGO = { onLight: fullLogoBlack, onDark: fullLogoWhite };

/**
 * The company's logo when it has one, otherwise the Aslan logo for the same ground. A logo
 * that fails to load falls back to the Aslan logo too, everywhere it shows, so a broken image
 * never shows and "Powered by Aslan" drops with it.
 */
function BrandLogo({ variant = 'onLight', ...imageProps }: BrandLogoProps) {
  const branding = useCompanyBranding();
  const logoUrl = companyLogoFor(branding, variant);

  const handleError = () => {
    addBreadcrumb({
      category: 'branding',
      level: 'warning',
      message: 'Company logo failed to load; showing the Aslan logo',
      data: { variant },
    });
    if (logoUrl) {
      branding.markLogoFailed(logoUrl);
    }
  };

  if (logoUrl) {
    return (
      <Image
        src={logoUrl}
        alt="Company logo"
        objectFit="contain"
        onError={handleError}
        {...imageProps}
      />
    );
  }

  return <Image src={ASLAN_LOGO[variant]} alt="Aslan" {...imageProps} />;
}

export default BrandLogo;
